import React, {useMemo, useState, useCallback, useContext} from "react";
import moment from "moment";
import { DateFormats } from "../../components/constants/securspace-constants";
import { PersonAndBoxMessage } from "../../components/messages/PersonAndBoxMessage";
import { useSnackbar } from "../../context/snackbar-context";
import { useSnackbarDisplayOnError } from "../../hooks/error/useSnackbarDisplayOnError";
import { useBindSearchQuery } from "../../hooks/useBindSearchQuery";
import { useGetBoundReportPaginated } from "../../hooks/request/useGetBoundReportPaginated";
import { useReportDataSummaryCollapseByIndex}  from "../../hooks/report/useReportDataSummaryCollapseByIndex";
import { BoundTablePagination } from "../../components/common/inputs/boundInputs";
import { PendingInventoryActivityVO } from "../../types/model/valueObject/pendinginventoryactivity/PendingInventoryActivityVO";
import { SupplierPendingInventoryActivitySortFilter, SupplierPendingInventoryActivitySearchParams } from "../../components/supplier/pendingInventoryActivity/SupplierPendingInventoryActivitySortFilter";
import { SupplierPendingInventoryActivityCardListWithLoader } from "../../components/supplier/pendingInventoryActivity/SupplierPendingInventoryActivityCardList";
import { EditPendingInventoryActivityPanel } from "../../components/supplier/pendingInventoryActivity/EditPendingInventoryActivityPanel";
import { SupplierPendingInventoryActivityDialogWrapper } from "../../components/supplier/pendingInventoryActivity/SupplierPendingInventoryActivityDialogWrapper";
import { supplierPendingInventoryActivityReportEndpoints as reportEndpoints } from "../../requests/api-endpoints";
import { ReportDialogSetterProps, getDefaultReportDialog, ReportDialogActionCloseProps } from "../../interfaces";
import { AppContext } from "../../context/app-context";
import { ObservableVariables } from "../../components/constants/observable-constants";
import {
  ReportDataSummary
} from "../../components/report/redesigned";
import {
  SecurSpaceContainer,
  SecurSpaceReportContainer,
  SecurSpaceReportTitle
} from "../../components/common";

// a default query MUST be defined outside of any components to avoid re-initialization on each render
const DEFAULT_QUERY: SupplierPendingInventoryActivitySearchParams = {
  status: 'PENDING',
  eventType: '',
  locationId: '',
  errorCode: '',
  bookingNumber: '',
  equipmentNumber: '',
  startDate: moment().subtract(1, 'month').format(DateFormats.DAY),
  endDate: moment().format(DateFormats.DAY),
  sortBy: "EVENT_DATETIME",
  sortDir: 'desc',
  page: 0,
  size: 10,
};

const SupplierPendingInventoryActivityReport = () => {
  const appContext = useContext(AppContext);
  const { snackbarShowMessage } = useSnackbar();

  // state hooks
  const [reportDialog, setReportDialog] = useState<ReportDialogSetterProps<PendingInventoryActivityVO>>(getDefaultReportDialog());
  const [reportQuery, setReportQuery] = useBindSearchQuery(DEFAULT_QUERY);

  // Edit panel state
  const [editPanelOpen, setEditPanelOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<PendingInventoryActivityVO | null>(null);

  // Api hooks
  const {
    reportPayload,
    pageInfo,
    isLoading,
    error,
    doRequest
  } =  useGetBoundReportPaginated<PendingInventoryActivityVO[]>({
    requestUrl: reportEndpoints.search,
    requestParams: reportQuery
  });

  const {
    collapseByIndex: expandByIndex, // TODO yf when redesigns slow down, rename this
    handleToggleCollapse: handleToggleExpand, // TODO yf same as above
    hasExpandedItems,
    setExpandedItems,
    setExpandedAll,
    handleExpandAll,
  } = useReportDataSummaryCollapseByIndex({ data: reportPayload });

  // error hooks
  useSnackbarDisplayOnError({error, snackbarShowMessage });

  // conditional display hooks
  // Not gated on `isLoading`: the previous payload survives a refetch, so
  // leaving this on keeps the empty state put instead of blinking out and
  // back on every filter change. It is undefined until the first load, so it
  // cannot flash before there is an answer.
  const shouldShowPersonAndBox = useMemo(() => {
    return !error && reportPayload?.length === 0
  }, [error, reportPayload]);

  const showPagination = useMemo(() => {
    return !!(reportPayload?.length && reportPayload.length > 0);
  }, [reportPayload])

  // change handlers
  const handleFilterChange = (newQuery: SupplierPendingInventoryActivitySearchParams) => {
    const updatedQuery = {
      ...reportQuery,
      ...newQuery
    };
    if (!newQuery.page) {
      // reset to first page on filter change if it is not being explicitly set
      updatedQuery.page = 0;
    }

    setReportQuery(updatedQuery);
  }

  // Edit panel handlers
  const handleEdit = useCallback((item: PendingInventoryActivityVO) => {
    setSelectedItem(item);
    setEditPanelOpen(true);
  }, []);

  const handleEditPanelClose = useCallback(() => {
    setEditPanelOpen(false);
    setSelectedItem(null);
  }, []);

  /**
   * Shared post-action callback: refreshes the report data and the pending activity badge count.
   */
  const refreshAfterAction = useCallback(() => {
    doRequest?.();
    ObservableVariables.PENDING_INVENTORY_ACTIVITY_COUNT.refreshData(appContext);
  }, [doRequest, appContext]);

  const handleDialogClose = (
    closeProps?: ReportDialogActionCloseProps<PendingInventoryActivityVO>
  ) => {
    switch (closeProps?.action) {
      case 'SUCCESS':
        refreshAfterAction();
        setReportDialog(getDefaultReportDialog());
        break;
      default:
        setReportDialog(getDefaultReportDialog());
    }
  }

  return (
    <SecurSpaceContainer>
      <SecurSpaceReportContainer>
        <SecurSpaceReportTitle>Pending Inventory Activity</SecurSpaceReportTitle>
        <SupplierPendingInventoryActivitySortFilter
          query={reportQuery}
          onChange={handleFilterChange}
        />
        <ReportDataSummary
          visible={!shouldShowPersonAndBox}
          count={pageInfo?.count || 0}
          title={"All"}
          isShowCollapseButton={true}
          hasExpandedItems={hasExpandedItems}
          setExpandedItems={setExpandedItems}
          setExpandedAll={setExpandedAll}
          handleExpandAll={handleExpandAll}
        />
        <SupplierPendingInventoryActivityCardListWithLoader
          visible={!error}
          isLoading={isLoading}
          items={reportPayload}
          expandByIndex={expandByIndex}
          onToggleExpand={handleToggleExpand}
          onEdit={handleEdit}
          onOpenDialog={setReportDialog}
        />
        <SupplierPendingInventoryActivityDialogWrapper
          dialog={reportDialog}
          onClose={handleDialogClose}
        />
        <PersonAndBoxMessage
          visible={shouldShowPersonAndBox }
        />
        <BoundTablePagination<SupplierPendingInventoryActivitySearchParams>
          visible={showPagination}
          pageInfo={pageInfo}
          onChange={handleFilterChange}
        />
      </SecurSpaceReportContainer>
      <EditPendingInventoryActivityPanel
        open={editPanelOpen}
        item={selectedItem}
        onClose={handleEditPanelClose}
        onActionSuccess={refreshAfterAction}
      />
    </SecurSpaceContainer>
  );
};

export default SupplierPendingInventoryActivityReport;
